import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, Filter } from 'lucide-react';

/**
 * Sticky top bar with Category, Wattage, CCT, IP quick filters
 * Per design guidelines: always visible, drives primary filtering
 */
export const QuickSpecsBar = ({
  categories = [],
  wattageRanges = [],
  cctOptions = [],
  ipRatings = [],
  cutoutRanges = [],
  dimmingOptions = [
    { value: 'dimmable', label: 'Dimmable' },
    { value: 'non-dimmable', label: 'Non-Dimmable' }
  ],
  
  // State
  selectedCategory,
  selectedWattageRanges,
  selectedCCTs,
  selectedIPRatings,
  selectedCutoutRanges = [],
  selectedDimming,
  
  // Handlers
  onCategoryChange,
  onWattageRangeToggle,
  onCCTToggle,
  onIPToggle,
  onCutoutRangeToggle,
  onDimmingChange,
  
  // Disabled state
  disabled = false
}) => {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const uniqueCategories = Object.values(
    (categories || []).reduce((acc, cat) => {
      const prev = acc[cat.slug];
      if (!prev || (cat.product_count || 0) > (prev.product_count || 0)) acc[cat.slug] = cat;
      return acc;
    }, {})
  );
  const [wattageOpen, setWattageOpen] = useState(false);
  const [cctOpen, setCctOpen] = useState(false);
  const [ipOpen, setIpOpen] = useState(false);
  const [cutoutOpen, setCutoutOpen] = useState(false);
  const [dimmingOpen, setDimmingOpen] = useState(false);

  return (
    <div 
      className="relative z-30 bg-background/95 backdrop-blur-sm border-b border-border"
      data-testid="quick-specs-bar"
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mr-2 w-full sm:w-auto">
            QUICK SPECS
          </span>
          
          {/* Category Select */}
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={disabled}
                data-testid="quick-specs-category-trigger"
                className="h-8 border-dashed"
              >
                <Filter className="w-3 h-3 mr-2" />
                Category
                {selectedCategory && (
                  <Badge variant="secondary" className="ml-2">
                    1
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[280px] sm:w-[280px] p-0 bg-background" align="start">
              <Command>
                <CommandInput data-testid="quick-specs-category-search" placeholder="Search category..." />
                <CommandList className="max-h-[320px]">
                  <CommandEmpty>No category found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      data-testid="quick-specs-category-all"
                      onSelect={() => {
                        onCategoryChange('');
                        setCategoryOpen(false);
                      }}
                    >
                      <div className="flex items-center w-full">
                        <span className="flex-1">All Categories</span>
                        {!selectedCategory && <Check className="w-4 h-4" />}
                      </div>
                    </CommandItem>
                    {uniqueCategories.map((cat) => (
                      <CommandItem
                        key={cat.slug}
                        onSelect={() => {
                          onCategoryChange(cat.slug);
                          setCategoryOpen(false);
                        }}
                        data-testid={`quick-specs-category-option-${cat.slug}`}
                      >
                        <div className="flex items-center w-full">
                          <span className="flex-1">{cat.name}</span>
                          {cat.product_count !== undefined && (
                            <Badge variant="outline" className="text-xs mr-2">
                              {cat.product_count}
                            </Badge>
                          )}
                          {selectedCategory === cat.slug && <Check className="w-4 h-4" />}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Wattage Range */}
          <Popover open={wattageOpen} onOpenChange={setWattageOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={disabled}
                data-testid="quick-specs-wattage-trigger"
                className="h-8 border-dashed"
              >
                <Filter className="w-3 h-3 mr-2" />
                Power
                {selectedWattageRanges.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedWattageRanges.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[240px] sm:w-[240px] p-0 bg-background border-border" align="start">
              <Command className="bg-background">
                <CommandInput data-testid="quick-specs-wattage-search" placeholder="Search wattage..." className="bg-background" />
                <CommandList className="max-h-[320px] bg-background">
                  <CommandEmpty>No wattage ranges found.</CommandEmpty>
                  <CommandGroup>
                    {wattageRanges.map((range) => {
                      const isSelected = selectedWattageRanges.includes(range.label);
                      return (
                        <CommandItem
                          key={range.label}
                          onSelect={() => {
                            onWattageRangeToggle(range.label);
                          }}
                          data-testid={`quick-specs-wattage-option-${range.label}`}
                        >
                          <div className="flex items-center w-full">
                            <span className="flex-1">{range.label}</span>
                            {range.count !== undefined && (
                              <Badge variant="outline" className="text-xs mr-2">
                                {range.count}
                              </Badge>
                            )}
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Cut-Out Size Multi-Select — match an existing ceiling opening */}
          {cutoutRanges.length > 0 && (
            <Popover open={cutoutOpen} onOpenChange={setCutoutOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  data-testid="quick-specs-cutout-trigger"
                  className="h-8 border-dashed"
                >
                  Cut-Out
                  {selectedCutoutRanges.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedCutoutRanges.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[calc(100vw-2rem)] max-w-[240px] sm:w-[240px] p-0 bg-background border-border" align="start">
                <Command className="bg-background">
                  <CommandInput data-testid="quick-specs-cutout-search" placeholder="Search cut-out size..." className="bg-background" />
                  <CommandList className="max-h-[320px] bg-background">
                    <CommandEmpty>No cut-out sizes found.</CommandEmpty>
                    <CommandGroup>
                      {cutoutRanges.map((range) => {
                        const isSelected = selectedCutoutRanges.includes(range.label);
                        return (
                          <CommandItem
                            key={range.label}
                            onSelect={() => onCutoutRangeToggle(range.label)}
                            data-testid={`quick-specs-cutout-option-${range.label}`}
                          >
                            <div className="flex items-center w-full">
                              <span className="flex-1">{range.label}</span>
                              {range.count !== undefined && (
                                <Badge variant="outline" className="text-xs mr-2">
                                  {range.count}
                                </Badge>
                              )}
                              {isSelected && <Check className="w-4 h-4" />}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          {/* CCT Multi-Select */}
          <Popover open={cctOpen} onOpenChange={setCctOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={disabled}
                data-testid="quick-specs-cct-trigger"
                className="h-8 border-dashed"
              >
                CCT
                {selectedCCTs.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedCCTs.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[240px] sm:w-[240px] p-0 bg-background border-border" align="start">
              <Command className="bg-background">
                <CommandInput data-testid="quick-specs-cct-search" placeholder="Search CCT..." className="bg-background" />
                <CommandList className="max-h-[320px] bg-background">
                  <CommandEmpty>No CCT options found.</CommandEmpty>
                  <CommandGroup>
                    {cctOptions.map((cctItem) => {
                      const cctValue = typeof cctItem === 'object' ? cctItem.value : cctItem;
                      const cctLabel = typeof cctItem === 'object' ? (cctItem.label || `${cctItem.value}K`) : cctItem;
                      const isSelected = selectedCCTs.includes(cctValue);
                      return (
                        <CommandItem
                          key={cctValue}
                          onSelect={() => {
                            onCCTToggle(cctValue);
                          }}
                          data-testid={`quick-specs-cct-option-${cctValue}`}
                        >
                          <div className="flex items-center w-full">
                            <span className="flex-1">{cctLabel}</span>
                            {typeof cctItem === 'object' && cctItem.count !== undefined && (
                              <Badge variant="outline" className="text-xs mr-2">
                                {cctItem.count}
                              </Badge>
                            )}
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* IP Rating Multi-Select */}
          <Popover open={ipOpen} onOpenChange={setIpOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={disabled}
                data-testid="quick-specs-ip-trigger"
                className="h-8 border-dashed"
              >
                IP Rating
                {selectedIPRatings.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedIPRatings.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[220px] sm:w-[220px] p-0 bg-background border-border" align="start">
              <Command className="bg-background">
                <CommandInput data-testid="quick-specs-ip-search" placeholder="Search IP..." className="bg-background" />
                <CommandList className="max-h-[320px] bg-background">
                  <CommandEmpty>No IP ratings found.</CommandEmpty>
                  <CommandGroup>
                    {ipRatings.map((ipItem) => {
                      const ipValue = typeof ipItem === 'object' ? ipItem.value : ipItem;
                      const ipLabel = typeof ipItem === 'object' ? (ipItem.label || ipItem.value) : ipItem;
                      const isSelected = selectedIPRatings.includes(ipValue);
                      return (
                        <CommandItem
                          key={ipValue}
                          onSelect={() => {
                            onIPToggle(ipValue);
                          }}
                          data-testid={`quick-specs-ip-option-${ipValue}`}
                        >
                          <div className="flex items-center w-full">
                            <span className="flex-1">{ipLabel}</span>
                            {typeof ipItem === 'object' && ipItem.count !== undefined && (
                              <Badge variant="outline" className="text-xs mr-2">
                                {ipItem.count}
                              </Badge>
                            )}
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Dimming/Control Select */}
          <Popover open={dimmingOpen} onOpenChange={setDimmingOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={disabled}
                data-testid="quick-specs-dimming-trigger"
                className="h-8 border-dashed"
              >
                <Filter className="w-3 h-3 mr-2" />
                Control
                {selectedDimming && (
                  <Badge variant="secondary" className="ml-2">
                    1
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[220px] sm:w-[220px] p-0 bg-background border-border" align="start">
              <Command className="bg-background">
                <CommandList className="max-h-[240px] bg-background">
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      data-testid="quick-specs-dimming-all"
                      onSelect={() => {
                        onDimmingChange('');
                        setDimmingOpen(false);
                      }}
                    >
                      <div className="flex items-center w-full">
                        <span className="flex-1">All Products</span>
                        {!selectedDimming && <Check className="w-4 h-4" />}
                      </div>
                    </CommandItem>
                    {dimmingOptions.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        onSelect={() => {
                          onDimmingChange(opt.value);
                          setDimmingOpen(false);
                        }}
                        data-testid={`quick-specs-dimming-option-${opt.value}`}
                      >
                        <div className="flex items-center w-full">
                          <span className="flex-1">{opt.label}</span>
                          {selectedDimming === opt.value && <Check className="w-4 h-4" />}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};
